// tabs/defenses.ts — client-side port of the PC sheet's Defenses tab:
//   options/game/character_sheet_display/defenses/_defenses_inc.php  (+ its
//   options/game/character_sheet_display/hunger_thirst/hunger_thirst_bars_inc.php include)
//
// Renders the main-sheet variant (NOT the side-info variant — that layout swap is
// driven by $opened_in_side_info on the PHP side and is out of scope for the pane
// mount). PARITY notes inline against the PHP line for line.
//
// The "special defenses" button grid is NOT re-implemented here: it is already a
// shipped TS island (renderStatusEffectButtonsInline, info-panel/creature-buttons.ts)
// that reads the same casterCache blob. We reproduce only the PHP gate (which decides
// button-grid vs the "No special defenses found" message) and delegate the grid.

import type { CachedCaster } from '../types';
import { computedCreature, num, sanitizeDomId, isPlayerCharacter } from '../read-helpers';

// icon_map('arcane_threshold') links to this catalog item (PHP _defenses_inc.php:17).
const ARCANE_THRESHOLD_ITEM_ID = 'DF96C189-93BC-9EB8-B4D6-8FFC3D8A8F3A';

// The conditions_arr gate in _defenses_inc.php:27-38 (verbatim order). NOTE: PHP's
// gate list omits contesting_roll_dis_vs_spells even though the button island renders
// it — we mirror the PHP GATE here so the empty-state decision matches exactly.
const CONDITION_LAYERS: ReadonlyArray<string> = [
    'damage_type_vulnerability',
    'damage_type_resistance',
    'damage_type_immunity',
    'immune_status_effect',
    'status_effect_permanent',
    'contesting_roll_adv_vs_status_effect',
    'contesting_roll_dis_vs_status_effect',
    'contesting_roll_adv_vs_spells',
];

/** layer_value($caster, 'arcane_threshold') ?? 0 — bridged to computed.creature.spellcasting. */
function arcaneThreshold(caster: CachedCaster): number {
    const bridged = computedCreature(caster, 'spellcasting', 'arcane_threshold');
    if (bridged !== undefined && bridged !== null) return num(bridged);
    const layer = ftLayerValue(caster, 'arcane_threshold');
    return layer === undefined || layer === null ? 0 : num(layer);
}

/** Total spirit (the mana cap / spellcasting pool). computed.creature.spirit is the rich
 *  {total, base, breakdown…} object post-reshape; legacy blobs carry a scalar slot. */
function spiritTotal(caster: CachedCaster): number {
    const rich = caster.computed?.creature?.spirit;
    if (rich !== undefined && rich !== null) {
        return num(rich && typeof rich === 'object' && !Array.isArray(rich) ? (rich as Record<string, unknown>).total : rich);
    }
    const layer = ftLayerValue(caster, 'spirit');
    if (layer === undefined || layer === null) return 0;
    return num(layer && typeof layer === 'object' && !Array.isArray(layer) ? (layer as Record<string, unknown>).total : layer);
}

/** layer_value($caster, '<which>_meter_max') — bridged to computed.creature.meters, else carrier-walked layer. */
function meterMax(caster: CachedCaster, which: 'hunger' | 'thirst'): number | null {
    const bridged = computedCreature(caster, 'meters', `${which}_max`);
    if (bridged !== undefined && bridged !== null) return num(bridged);
    const layer = ftLayerValue(caster, `${which}_meter_max`);
    return layer === undefined || layer === null ? null : num(layer);
}

const statusTargetId = (casterId: string): string => `ft_pc_def_status_${sanitizeDomId(casterId)}`;

/** Port of hunger_thirst_bars_inc.php — empty string when the PHP gate fails. */
function buildHungerThirstHtml(caster: CachedCaster, casterId: string): string {
    // PC-only feature. item_type is the canonical discriminator; isPlayerCharacter() reads it from the
    // encounter_creatures bucket (a caster blob has no top-level items bucket, so ftItemsCol returns
    // undefined here — the trap this used to work around). Keep the player_characters-bucket OR as a
    // belt-and-suspenders fallback; the real filter is the meterMax check below anyway — the SERVER only
    // computes hunger/thirst max for player_characters (creature_finalize_helpers.php:102), so a non-PC
    // blob bails there regardless.
    if (!isPlayerCharacter(caster) && !caster.player_characters) return '';

    const hungerMax = meterMax(caster, 'hunger');
    const thirstMax = meterMax(caster, 'thirst');
    // hunger/thirst max come from SizeType for any sized PC (creature_finalize_helpers.php:101) — they
    // resolve on the standalone sheet too, not just in-encounter. Null only when the PC has no size
    // data → feature not applicable.
    if (hungerMax === null || thirstMax === null) return '';

    // Current value: in-encounter uses the live meter (encounter_creatures); on the standalone sheet
    // (no encounter row) default to FULL, mirroring mana/vitality's "show full when there's no encounter
    // creature" pattern — so the meters ALWAYS render on a PC sheet (owner 2026-06-23). A real persisted
    // out-of-encounter current would be a separate data-model change.
    const ec = caster.encounter_creatures;
    const hungerRaw = ec?.hunger_meter_current;
    const thirstRaw = ec?.thirst_meter_current;
    const hungerCur = (hungerRaw === undefined || hungerRaw === null) ? hungerMax : num(hungerRaw);
    const thirstCur = (thirstRaw === undefined || thirstRaw === null) ? thirstMax : num(thirstRaw);

    const hungerPct = hungerMax > 0 ? Math.floor((hungerCur / hungerMax) * 100) : 0;
    const thirstPct = thirstMax > 0 ? Math.floor((thirstCur / thirstMax) * 100) : 0;

    const cid = escapeHtml(casterId);
    // Plain clickable icon (no bordered box) — removes the redundant border that doubled with the bar's
    // own border inside the already-bordered Defenses card (owner: "border hell"). Opens the TS info
    // island (renderHungerInfoPanel / renderThirstInfoPanel) — a blob-driven panel that repaints on the
    // caster_update WS push, NOT the old PHP AJAX panel that force-refreshed the sheet on adjust.
    const meterButton = (icon: string, panelFn: 'renderHungerInfoPanel' | 'renderThirstInfoPanel'): string => `
                <div class="p-1 mr-1 d-inline-flex align-items-center" role="button"
                    onclick="if(window.forceOpenSideInfoPanel)forceOpenSideInfoPanel(); if(window.${panelFn})window.${panelFn}('${cid}', 'div_refresh_side_info');">
                    <div>${iconMap(icon)}</div>
                </div>`;

    const bar = (label: string, barCls: string, pct: number, cur: number, max: number): string => `
            <div class="flex-grow-1">
                <div class="progress border position-relative" role="progressbar" aria-label="${label}"
                    aria-valuenow="${pct}" aria-valuemin="0" aria-valuemax="100">
                    <div class="progress-bar ${barCls} text-dark overflow-visible" style="width: ${pct}%">${cur} / ${max}</div>
                </div>
            </div>`;

    return `<div class="col-12">
        <div class="d-flex align-items-center justify-content-center mb-1">
            ${meterButton('hunger', 'renderHungerInfoPanel')}
            ${bar('hunger_bar', 'bg-secondary', hungerPct, hungerCur, hungerMax)}
        </div>
        <div class="d-flex align-items-center justify-content-center mb-1">
            ${meterButton('thirst', 'renderThirstInfoPanel')}
            ${bar('thirst_bar', 'bg-info', thirstPct, thirstCur, thirstMax)}
        </div>
    </div>`;
}

/** Build the inner HTML for the Defenses pane mount (main-sheet variant).
 *  2026-06-10 makeover (owner-flagged): header banner matching the other tabs, the bare
 *  arcane-threshold icon+number became a LABELED chip, and the category rows below get
 *  text labels (creature-buttons.ts) — the icon-only cells read as mystery glyphs. */
export function buildDefensesHtml(caster: CachedCaster, casterId: string): string {
    const hungerThirst = buildHungerThirstHtml(caster, casterId);
    const arcane = arcaneThreshold(caster);
    const found = CONDITION_LAYERS.some((layer) => ftLayerHas(caster, layer));

    const bottom = found
        ? `<div id="${escapeHtml(statusTargetId(casterId))}"></div>`
        : `<div class='text-center text-secondary py-4'><h6 class="mb-0">No special defenses.</h6></div>`;

    // Arcane Threshold chip — labeled, click-through to the rule item. Only shown when the
    // creature can actually CAST (spirit total > 0): arcane fatigue is meaningless for a
    // non-caster, and rendering it on every creature was noise (owner-flagged 2026-06-10).
    const arcaneChip = spiritTotal(caster) > 0 ? `
        <div class="ft-defenses__arcane d-inline-flex align-items-center border rounded px-2 py-1 gap-2" role="button"
            onclick="openItemInfo('${ARCANE_THRESHOLD_ITEM_ID}');">
            ${iconMap('arcane_threshold', 22, 22, 'Spell tiers at or below this cast without Arcane Fatigue')}
            <small class="text-secondary">Arcane Threshold</small>
            <span class="badge ${arcane > 0 ? 'bg-info text-dark' : 'bg-dark border'}">${escapeHtml(String(arcane))}</span>
        </div>` : '';

    // Order (owner 2026-06-23): special-defense bands, then the Arcane Threshold chip, then the
    // hunger/thirst vital meters NESTED BELOW Arcane Threshold (the PC-only "survival" section —
    // it isn't really a defense, but lives here rather than getting its own tab).
    // No outer border/rounded: the tab-content container already draws a border
    // (table_item_player_character_inc.php:812 `tab-content border`); a card frame here
    // doubled it ("border hell", owner 2026-06-23). The header banner's own bg keeps the
    // section visually distinct without a second frame.
    return `<div class="ft-defenses ft-core-abilities mb-2 px-1" data-ft-defenses-has-special="${found ? '1' : '0'}">
    <h5 class="text-start ps-3 mt-1 mb-0 py-2 text-light bg-gradient rounded-top">${iconMap('defenses', 22, 22)} Defenses</h5>
    <div class="m-2">${bottom}</div>
    ${arcaneChip !== '' ? `<div class="d-flex flex-wrap align-items-center gap-2 m-2">${arcaneChip}</div>` : ''}
    ${hungerThirst !== '' ? `<div class="ft-defenses__survival m-2">${hungerThirst}</div>` : ''}
</div>`;
}

/** Post-insert hydration: fill the special-defenses button grid via the shipped island. */
export function hydrateDefenses(_container: HTMLElement, caster: CachedCaster, casterId: string): void {
    const found = CONDITION_LAYERS.some((layer) => ftLayerHas(caster, layer));
    if (!found) return;
    if (typeof window.renderStatusEffectButtonsInline === 'function') {
        try {
            void window.renderStatusEffectButtonsInline(casterId, statusTargetId(casterId));
        } catch (e) {
            console.error('[pc-sheet] defenses status-button hydrate failed', e);
        }
    }
}
